import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { HttpService } from 'core/services/http';
import { getCroppedImageBlob } from '../../utils/profilePhotoCrop';

const CROP_AREA_SIZE = 280;

const ProfilePhotoCropDialog = ({ open, imageSrc, profile, onClose, onUploaded }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const resetLocal = () => {
    setError('');
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  const handleClose = () => {
    if (uploading) return;
    resetLocal();
    onClose();
  };

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      enqueueSnackbar('Adjust the crop area and try again', { variant: 'warning' });
      return;
    }
    setUploading(true);
    setError('');
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('image', file);
      if (profile?.first_name) formData.append('first_name', profile.first_name);
      if (profile?.last_name) formData.append('last_name', profile.last_name);

      const service = new HttpService();
      const response = await service.post('/auth/profile/update/', formData, true);

      if (response?.error) {
        setError(response.error);
        enqueueSnackbar(response.error || 'Upload failed', { variant: 'error' });
        return;
      }

      enqueueSnackbar('Profile photo updated successfully', { variant: 'success' });
      setUploading(false);
      resetLocal();
      if (onUploaded) onUploaded();
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to upload image';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={600}>
          Crop profile photo
        </Typography>
        <IconButton onClick={handleClose} disabled={uploading} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', pt: 1 }}>
          {imageSrc && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: CROP_AREA_SIZE,
                height: CROP_AREA_SIZE,
                bgcolor: 'grey.900',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
          )}
          <Box sx={{ width: '100%', maxWidth: CROP_AREA_SIZE, px: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Zoom
            </Typography>
            <Slider
              value={zoom}
              onChange={(_, value) => setZoom(value)}
              min={1}
              max={3}
              step={0.05}
              disabled={uploading}
              aria-label="Crop zoom"
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Number(v.toFixed(2))}×`}
            />
          </Box>
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={uploading || !imageSrc}>
          {uploading ? <CircularProgress size={22} color="inherit" /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePhotoCropDialog;
